import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { apiError } from "../../lib/apiError";
import { useModalHistoryEntry } from "../../lib/modalHistory";

export type SampleStatus = { present: number; total: number };

type View = "main" | "confirm" | "result";

type Props = {
  open: boolean;
  initial:
    | { kind: "main"; status: SampleStatus }
    | { kind: "result"; title: string; message: string }
    | null;
  onClose: () => void;
  onDataChanged?: () => void;
  busy: boolean;
  setBusy: (busy: boolean) => void;
};

function presentLabel(count: number): string {
  return count === 1 ? "1 przykładowa owca" : `${count} przykładowych owiec`;
}

export function SampleDataButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="topbar__action topbar__action--labeled"
      data-testid="sample-data-btn"
      title="Wczytaj lub usuń przykładowe dane stada"
      aria-label="Przykładowe dane stada"
      disabled={disabled}
      onClick={onClick}
    >
      <svg
        className="topbar__action-icon"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <ellipse cx="12" cy="5" rx="7" ry="3" />
        <path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
        <path d="M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
        <path d="M12 14v4" />
        <path d="M9.5 16.5 12 19l2.5-2.5" />
      </svg>
      <span className="topbar__action-label">Przykłady</span>
    </button>
  );
}

export function SampleDataModal({ open, initial, onClose, onDataChanged, busy, setBusy }: Props) {
  const [view, setView] = useState<View>("main");
  const [status, setStatus] = useState<SampleStatus>({ present: 0, total: 15 });
  const [title, setTitle] = useState("Przykładowe dane");
  const [text, setText] = useState("");
  const [statusNote, setStatusNote] = useState<string | null>(null);

  function finishClose() {
    onClose();
  }

  useModalHistoryEntry(open, finishClose);

  useEffect(() => {
    if (!open || !initial) {
      return;
    }
    if (initial.kind === "result") {
      setView("result");
      setTitle(initial.title);
      setText(initial.message);
      setStatusNote(null);
      return;
    }
    applyMain(initial.status);
  }, [open, initial]);

  function applyMain(next: SampleStatus) {
    setStatus(next);
    setView("main");
    setTitle("Przykładowe dane");
    setText(
      `Wczytaj ${next.total} przykładowych owiec na swoje konto. Istniejące zwierzęta nie zostaną nadpisane — duplikaty będą pominięte.`,
    );
    setStatusNote(
      next.present > 0
        ? `Na koncie jest już ${presentLabel(next.present)} z tego zestawu. Możesz wczytać brakujące lub usunąć cały zestaw.`
        : null,
    );
  }

  function showResult(nextTitle: string, message: string) {
    setView("result");
    setTitle(nextTitle);
    setText(message);
    setStatusNote(null);
  }

  async function onLoad() {
    setBusy(true);
    const result = await api.POST("/api/animals/sample-data", { body: {} });
    setBusy(false);
    if (result.error) {
      showResult("Błąd", apiError(result.error, "Nie udało się wczytać danych."));
      return;
    }
    const data = result.data as { created: number; skipped: number; total: number };
    showResult(
      "Gotowe",
      `Dodano: ${data.created}\nPominięto (już w bazie): ${data.skipped}\nW pliku: ${data.total}`,
    );
    onDataChanged?.();
  }

  async function onClear() {
    setBusy(true);
    const result = await api.DELETE("/api/animals/sample-data");
    setBusy(false);
    if (result.error) {
      showResult("Błąd", apiError(result.error, "Nie udało się usunąć przykładowych danych."));
      return;
    }
    const data = result.data as { deleted: number };
    showResult(
      "Usunięto",
      data.deleted === 1
        ? "Usunięto 1 przykładową owcę z konta."
        : `Usunięto ${data.deleted} przykładowych owiec z konta.`,
    );
    onDataChanged?.();
  }

  if (!open) {
    return null;
  }

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sample-data-modal-title"
      data-testid="sample-data-modal"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          finishClose();
        }
      }}
    >
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <h3 className="modal__title" id="sample-data-modal-title">
          {title}
        </h3>
        <p className="modal__text" style={{ whiteSpace: "pre-line" }}>
          {text}
        </p>
        {statusNote ? <p className="modal__text modal__text--sm">{statusNote}</p> : null}

        {view === "main" ? (
          <div className="modal__actions modal__actions--sample">
            <button type="button" className="btn btn--ghost" onClick={finishClose}>
              Anuluj
            </button>
            {status.present >= 1 ? (
              <button
                type="button"
                className="btn btn--danger"
                data-testid="sample-data-clear"
                disabled={busy}
                onClick={() => {
                  setView("confirm");
                  setTitle("Usunąć przykładowe owce?");
                  setText(
                    `Zostanie usuniętych ${status.present} ${
                      status.present === 1 ? "owca" : "owiec"
                    } pochodzących z zestawu przykładowego. Tej operacji nie da się cofnąć.`,
                  );
                  setStatusNote(null);
                }}
              >
                Usuń przykładowe owce
              </button>
            ) : null}
            <button
              type="button"
              className="btn"
              data-testid="sample-data-load"
              disabled={busy}
              onClick={() => void onLoad()}
            >
              Wczytaj dane
            </button>
          </div>
        ) : null}

        {view === "confirm" ? (
          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={() => applyMain(status)}>
              Anuluj
            </button>
            <button
              type="button"
              className="btn btn--danger"
              data-testid="sample-data-confirm-delete"
              disabled={busy}
              onClick={() => void onClear()}
            >
              Tak, usuń
            </button>
          </div>
        ) : null}

        {view === "result" ? (
          <div className="modal__actions">
            <button
              type="button"
              className="btn"
              data-testid="sample-data-result-ok"
              onClick={finishClose}
            >
              OK
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export async function fetchSampleDataOpenState(): Promise<
  { kind: "main"; status: SampleStatus } | { kind: "result"; title: string; message: string }
> {
  if (!navigator.onLine) {
    return {
      kind: "result",
      title: "Brak połączenia",
      message:
        "Wczytanie lub usunięcie przykładowych danych wymaga połączenia z internetem.",
    };
  }
  const result = await api.GET("/api/animals/sample-data");
  if (result.error) {
    return {
      kind: "result",
      title: "Błąd",
      message: apiError(result.error, "Nie udało się sprawdzić stanu przykładowych danych."),
    };
  }
  const data = result.data as SampleStatus;
  return {
    kind: "main",
    status: { present: data.present ?? 0, total: data.total ?? 15 },
  };
}
